"use client";

import { useState, useEffect } from "react";
import { GroupSection } from "@/components/booking/GroupSection";
import { I18nProvider } from "@/components/booking/I18nProvider";
import { useVenuePageView } from "@/lib/hooks/useFacebookPixel";

interface GroupClientProps {
  bookingType: "private" | "company";
  venueId: string;
  lang?: string;
}

export default function GroupClient({
  bookingType,
  venueId,
  lang,
}: GroupClientProps) {
  const [venueData, setVenueData] = useState<{
    venueName: string | null;
    supportEmail: string | null;
    supportPhone: string | null;
  }>({
    venueName: null,
    supportEmail: null,
    supportPhone: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Track venue page view for Facebook Pixel
  const { trackVenuePageView } = useVenuePageView();

  useEffect(() => {
    const fetchVenueDetails = async () => {
      try {
        const response = await fetch(`/api/venues/${venueId}/details`);
        if (response.ok) {
          const data = await response.json();
          const venueName = data.data?.name || null;
          setVenueData({
            venueName: venueName,
            supportEmail: data.data?.email || null,
            supportPhone: data.data?.phone || null,
          });

          // Track venue page view to Facebook Pixel
          await trackVenuePageView(parseInt(venueId), venueName || undefined);
        }
      } catch (error) {
        console.error("Error fetching venue details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (venueId) {
      fetchVenueDetails();
    }
  }, [venueId, trackVenuePageView]);

  return (
    <I18nProvider lang={lang}>
      <GroupSection
        bookingType={bookingType}
        venueId={venueId}
        venueName={venueData.venueName || undefined}
        supportEmail={venueData.supportEmail}
        supportPhone={venueData.supportPhone}
      />
    </I18nProvider>
  );
}
